import type { ReactElement } from "react";
import {
  policyDisallowsEdgeWhitespace,
  type PasswordPolicyDescriptor,
} from "./first-credential-client";

/**
 * Shared 011-21 password-policy presentation, reused by every surface that
 * renders the server-advertised `PasswordPolicyDescriptor` (/set-password and
 * the 011-15 FR-050 "Create your password" group). Server authority: no
 * client hardcoded policy values, and no maximum is ever advertised.
 */

/** Inline error for a leading/trailing-whitespace miss (server rule mirror). */
export const PASSWORD_EDGE_WHITESPACE_MESSAGE = "Your password cannot start or end with a space.";

export interface PasswordRequirementsProps {
  policy: PasswordPolicyDescriptor;
  /** Container id referenced by the password control's `aria-describedby`. */
  id: string;
  /** Container classes; the default is the /set-password rendering. */
  className?: string;
}

/**
 * Server-provided policy descriptor rendered as the requirements associated
 * with the password field (aria-describedby).
 */
export function PasswordRequirements({
  policy,
  id,
  className = "mt-2 text-sm text-gray-600",
}: PasswordRequirementsProps): ReactElement {
  return (
    <div id={id} className={className}>
      <p>Your password must include:</p>
      <ul className="mt-1 list-inside list-disc">
        <li>At least {policy.minimumLength} characters</li>
        {policy.requireUppercase && <li>An uppercase letter</li>}
        {policy.requireLowercase && <li>A lowercase letter</li>}
        {policy.requireNumber && <li>A number</li>}
        {policy.requireSymbol && <li>A symbol</li>}
      </ul>
      {/* Prohibition, not an inclusion: rendered as help text beside the
          "must include" list, still inside the aria-describedby container. */}
      {policyDisallowsEdgeWhitespace(policy) && (
        <p className="mt-1">It cannot start or end with a space.</p>
      )}
    </div>
  );
}

/**
 * One-sentence restatement of the full policy (composition rules plus the
 * whitespace prohibition when advertised). Used wherever the contract asks
 * to restate the policy rather than echo server text — a client-side policy
 * miss and the server's `password_policy` 422.
 */
export function describePasswordPolicy(policy: PasswordPolicyDescriptor): string {
  const requirements: string[] = [];
  if (policy.requireUppercase) {
    requirements.push("an uppercase letter");
  }
  if (policy.requireLowercase) {
    requirements.push("a lowercase letter");
  }
  if (policy.requireNumber) {
    requirements.push("a number");
  }
  if (policy.requireSymbol) {
    requirements.push("a symbol");
  }
  const base = `Your password must be at least ${String(policy.minimumLength)} characters`;
  let sentence: string;
  if (requirements.length === 0) {
    sentence = `${base}.`;
  } else {
    const list =
      requirements.length === 1
        ? requirements[0]
        : `${requirements.slice(0, -1).join(", ")}, and ${requirements.at(-1) ?? ""}`;
    sentence = `${base} and include ${list ?? ""}.`;
  }
  return policyDisallowsEdgeWhitespace(policy)
    ? `${sentence} It cannot start or end with a space.`
    : sentence;
}
